import { ApiError } from './errors';
import { delegationTokenManager } from '@api/auth/delegationTokenManager';
import { delegationTokenConfig } from '@api/auth/config';
import { getLogger } from '@logging/logger';

function pathOf(url: string): string {
  try {
    if (url.startsWith('http://') || url.startsWith('https://')) return new URL(url).pathname;
  } catch {
    // fall through
  }
  return url.split('?')[0];
}

/**
 * Resolve relative URLs to absolute for specific prefixes when a base URL is provided.
 * Keeps URLs relative when no base is configured (so dev proxy can handle them).
 */
const resolveUrl = (input: RequestInfo | URL): string => {
  // biome-ignore lint/suspicious/noShadowRestrictedNames: ignore
  const toString = (u: RequestInfo | URL): string => (typeof u === 'string' ? u : u instanceof URL ? u.toString() : (u as Request).url);

  const { accountsEndpoints } = delegationTokenConfig;
  const urlString = toString(input);
  const isAbsolute = urlString.startsWith('http://') || urlString.startsWith('https://');
  if (isAbsolute) return urlString;

  // Only lift specific API prefixes to absolute, others remain relative
  const accountsBase = accountsEndpoints.baseUrl;
  if (accountsBase) {
    try {
      return new URL(urlString, accountsBase).toString();
    } catch {
      // Fallback to original if URL composition fails
      return urlString;
    }
  }
  return urlString;
};

/**
 * Determines if the request URL is for an accounts endpoint
 * Uses hostname and path pattern matching for accurate detection
 */
const isAccountsEndpoint = (url: RequestInfo | URL): boolean => {
  const urlString = typeof url === 'string' ? url : url.toString();
  const { accountsEndpoints } = delegationTokenConfig;

  try {
    // Handle absolute URLs
    if (urlString.startsWith('http://') || urlString.startsWith('https://')) {
      const urlObj = new URL(urlString);

      // Check path patterns for absolute URLs
      const isMatchingPath = accountsEndpoints.pathPatterns.some((pattern) => urlObj.pathname.includes(pattern));
      return isMatchingPath;
    }

    // Handle relative URLs - check path patterns
    const isMatchingPath = accountsEndpoints.pathPatterns.some((pattern) => urlString.includes(pattern));
    return isMatchingPath;
  } catch (error) {
    getLogger().warn('accounts endpoint url parse failed', {}, error);
    // Fallback to simple string matching
    return accountsEndpoints.pathPatterns.some((pattern) => urlString.includes(pattern));
  }
};

export const fetcher = async <T>(input: RequestInfo | URL, init?: RequestInit): Promise<T> => {
  const url = resolveUrl(input);
  const method = init?.method ?? 'GET';
  const path = pathOf(url);
  const start = performance.now();
  const headers: Record<string, string> = {
    ...(init?.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
    ...((init?.headers as Record<string, string>) || {}),
  };

  // Add Bearer token for accounts endpoints
  if (isAccountsEndpoint(url)) {
    try {
      const token = await delegationTokenManager.getToken();
      headers.Authorization = `Bearer ${token}`;
    } catch (error) {
      getLogger().error('delegation token request failed', { method, path }, error);
      throw new ApiError(401, 'Authentication required', { message: 'Failed to obtain delegation token' });
    }
  }

  getLogger().debug('api request', { method, path });
  const res = await fetch(url, {
    ...init,
    headers,
  });
  getLogger().debug('api response', { method, path, status: res.status, durationMs: Math.round(performance.now() - start) });

  // Handle 401 responses for accounts endpoints by refreshing token and retrying
  if (!res.ok && res.status === 401) {
    getLogger().info('api 401 retry', { method, path });
    try {
      // Clear the cached token and get a fresh one
      delegationTokenManager.clearToken();
      const newToken = await delegationTokenManager.getToken();

      // Retry the request with the new token
      const retryHeaders = {
        ...headers,
        Authorization: `Bearer ${newToken}`,
      };

      const retryRes = await fetch(url, {
        ...init,
        headers: retryHeaders,
      });

      if (retryRes.ok) {
        // Handle 204 No Content
        if (retryRes.status === 204) {
          return undefined as T;
        }
        return (await retryRes.json()) as T;
      }

      // If retry also failed, fall through to normal error handling
      return handleErrorResponse(retryRes);
    } catch (error) {
      getLogger().error('api 401 retry failed', { method, path }, error);
      throw new ApiError(401, 'Authentication failed', { message: 'Unable to refresh token' });
    }
  }

  if (!res.ok) {
    return handleErrorResponse(res);
  }

  // Handle 204 No Content
  if (res.status === 204) {
    return undefined as T;
  }

  return (await res.json()) as T;
};

async function handleErrorResponse(res: Response): Promise<never> {
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    body = await res.text().catch(() => undefined);
  }
  const message = extractErrorMessage(body) || res.statusText || `HTTP ${res.status}`;
  throw new ApiError(res.status, message, body);
}

function extractErrorMessage(body: unknown): string | undefined {
  if (typeof body === 'string') return body || undefined;
  if (body && typeof body === 'object') {
    const b = body as Record<string, unknown>;
    for (const key of ['message', 'error', 'title'] as const) {
      const v = b[key];
      if (typeof v === 'string' && v) return v;
    }
  }
  return undefined;
}
